import { useState, useEffect, useRef, Fragment } from "react";
import { useParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { Toast } from "primereact/toast";
import { ProgressSpinner } from "primereact/progressspinner";
import { PageLayout } from "../../layout/PageLayout/PageLayout";
import { ButtonInput } from "../../components/ButtonInput/ButtonInput";
import { TextInput } from "../../components/TextInput/TextInput";
import { DateInput } from "../../components/DateInput/DateInput";
import { DropdownInput } from "../../components/DropdownInput/DropdownInput";
import { PdfPreview } from "../../components/PdfPreview/PDFPreview";
import {
  getOneDeclaration,
  addNewDeclaration,
  updateOneDeclaration,
  getProcessingOrdersForDeclarations,
} from "../../services/FinishingCenterService";
import "./DeclarationOverviewPage.scss";
import { useTranslation } from "react-i18next";
import { AxiosError, AxiosResponse } from "axios";
import {
  ProcessingBatch,
  ProcessingOrderDetails,
} from "../../models/ProcessingOrder";
import { DeclarationDetails } from "../../models/Declaration";
import { Utility } from "../../core/Utility";
import { getDeclarationTemplate } from "../../document-templates/DeclarationTemplate";

export interface DeclarationPageProps {
  show?: string;
  buttonSaveStyle?: React.CSSProperties;
  buttonResetStyle?: React.CSSProperties;
}

export const DeclarationOverviewPage = (p: DeclarationPageProps) => {
  const { buttonSaveStyle, buttonResetStyle } = p;

  const { id } = useParams();
  const navigate = useNavigate();

  const { t } = useTranslation();

  const [updating, setUpdating] = useState<boolean>(false);
  const [loadingLookups, setLoadingLookups] = useState<boolean>(true);
  const [newDeclaration, setNewDeclaration] = useState<DeclarationDetails>(
    {} as DeclarationDetails,
  );
  const [orders, setOrders] = useState<ProcessingOrderDetails[]>([]);
  const [batches, setBatches] = useState<ProcessingBatch[]>([]);
  const [isPdfReady, setIsPdfReady] = useState(false);
  const [processing, setProcessing] = useState<boolean>(false);

  const selectedOrder = orders.find(
    (o) => o.id === (newDeclaration.orderId ?? newDeclaration.order?.id),
  );
  const [pdfData, setPdfData] = useState<any>();

  const downloadPdfDocument = useRef(null);
  const printPdfDocument = useRef(null);

  useEffect(() => {
    const loadLookupsAndOrders = async () => {
      try {
        setLoadingLookups(true);
        const ordersRes = await getProcessingOrdersForDeclarations();
        setOrders(ordersRes.data.result ?? []);
      } catch (err) {
        console.error(err);
        // @ts-ignore
        toast.current?.show({
          severity: "error",
          detail: t("Greška pri učitavanju naloga"),
          life: 3000,
        });
      } finally {
        setLoadingLookups(false);
      }
    };

    loadLookupsAndOrders();
  }, []);

  useEffect(() => {
    if (id) {
      setProcessing(true);
      setIsPdfReady(false);
      getOneDeclaration(id)
        .then((response: AxiosResponse) => {
          const newDeclaration = response.data.result;
          if (newDeclaration) {
            if (newDeclaration.issueDate) {
              newDeclaration.issueDate = new Date(newDeclaration.issueDate);
            }
            if (newDeclaration.validUntil) {
              newDeclaration.validUntil = new Date(newDeclaration.validUntil);
            }
            newDeclaration.amountKg = Utility.normalizeAmount(
              newDeclaration.amountKg,
            ) as any;
            setNewDeclaration(newDeclaration);
            setUpdating(true);
            setIsPdfReady(true);
          }
        })
        .catch((error: AxiosError) => {
          console.log(error);
        })
        .finally(() => {
          setProcessing(false);
        });
    } else {
      setIsPdfReady(true);
      setProcessing(false);
    }
  }, [id]);

  useEffect(() => {
    if (selectedOrder) {
      setBatches(selectedOrder.batches ?? []);
    }
  }, [selectedOrder]);

  useEffect(() => {
    if (!newDeclaration.batchId || batches.length === 0) return;
    const b = batches.find((b) => b.id === newDeclaration.batchId);
    if (b && !newDeclaration.batch) {
      setNewDeclaration((prev) => ({ ...prev, batch: b }));
    }
  }, [batches, newDeclaration.batchId]);

  useEffect(() => {
    const generatePdf = () => {
      if (!isPdfReady) return;
      const newPdf = getDeclarationTemplate(
        newDeclaration,
        selectedOrder as ProcessingOrderDetails,
      );
      setPdfData(newPdf);
    };

    const timer = setTimeout(generatePdf, 100);
    return () => clearTimeout(timer);
  }, [newDeclaration, isPdfReady, selectedOrder]);

  const toast = useRef(null);

  const {
    control,
    formState: { errors },
    handleSubmit,
    setValue,
  } = useForm({
    mode: "all",
  });

  const onSubmit = () => {
    const payload = {
      id: newDeclaration.id,
      declarationNumber: newDeclaration.declarationNumber,
      orderId: newDeclaration.orderId ?? newDeclaration.order?.id,
      batchId: newDeclaration.batchId ?? newDeclaration.batch?.id,
      productType: newDeclaration.productType,
      latinName: newDeclaration.latinName,
      sort: newDeclaration.sort,
      productionYear: newDeclaration?.productionYear,
      amountKg:
        newDeclaration.amountKg != null
          ? Number(newDeclaration.amountKg)
          : undefined,
      labName: newDeclaration.labName,
      reportNumber: newDeclaration.reportNumber,
      reportDate: newDeclaration.reportDate
        ? Utility.toMiddayDate(new Date(newDeclaration.reportDate))
        : undefined,
      purity:
        newDeclaration.purity != null
          ? Number(newDeclaration.purity)
          : undefined,
      moisture:
        newDeclaration.moisture != null
          ? Number(newDeclaration.moisture)
          : undefined,
      germination:
        newDeclaration.germination != null
          ? Number(newDeclaration.germination)
          : undefined,
      germinationEnergy:
        newDeclaration.germinationEnergy != null
          ? Number(newDeclaration.germinationEnergy)
          : undefined,
      thousandSeedMass:
        newDeclaration.thousandSeedMass != null
          ? Number(newDeclaration.thousandSeedMass)
          : undefined,
      otherSpecies:
        newDeclaration.otherSpecies != null
          ? Number(newDeclaration.otherSpecies)
          : undefined,
      weeds:
        newDeclaration.weeds != null ? Number(newDeclaration.weeds) : undefined,
      healthStatus: newDeclaration.healthStatus,
      validUntil: newDeclaration.validUntil
        ? Utility.toMiddayDate(new Date(newDeclaration.validUntil))
        : undefined,
      issueDate: newDeclaration.issueDate
        ? Utility.toMiddayDate(new Date(newDeclaration.issueDate))
        : undefined,
      labelsFrom: newDeclaration.labelsFrom,
      labelsTo: newDeclaration.labelsTo,
    };
    if (updating) {
      updateOneDeclaration(id as string, payload).then(
        (response: AxiosResponse) => {
          if (response.status === 200 || response.status === 201) {
            //@ts-ignore
            toast.current.show({
              severity: "success",
              detail: t(`Uspešno izmenjena deklaracija`),
              life: 3000,
            });
            navigate(`/declarations`);
          } else {
            //@ts-ignore
            toast.current.show({
              severity: "error",
              detail: t(`Izmena deklaracije nije snimljena, proverite podatke`),
              life: 3000,
            });
          }
        },
      );
    } else {
      addNewDeclaration(payload).then((response: AxiosResponse) => {
        if (response.status === 200 || response.status === 201) {
          //@ts-ignore
          toast.current.show({
            severity: "success",
            detail: t(`Uspešno dodata deklaracija`),
            life: 3000,
          });
          navigate(`/declarations`);
        } else {
          //@ts-ignore
          toast.current.show({
            severity: "error",
            detail: t(`Nova deklaracija nije snimljena, proverite podatke`),
            life: 3000,
          });
        }
      });
    }
  };

  const onError = () => {
    //@ts-ignore
    toast.current.show({
      severity: "error",
      detail: t(`Neispravno popunjena polja`),
      life: 3000,
    });
  };

  const handleResetForm = () => {
    setNewDeclaration({
      ...newDeclaration,
      orderId: "",
      orderNumber: "",
      batchId: "",
      productType: "",
      sort: "",
      amountKg: undefined,
      productionYear: undefined,
      labName: "",
      reportNumber: "",
      reportDate: undefined,
      purity: undefined,
      moisture: undefined,
      germination: undefined,
      germinationEnergy: undefined,
      thousandSeedMass: undefined,
      otherSpecies: undefined,
      weeds: undefined,
      healthStatus: "",
      validUntil: undefined,
      issueDate: undefined,
      labelsFrom: "",
      labelsTo: "",
    });
  };

  const handleNewDeclaration = (prop: string, value: any) => {
    setNewDeclaration((newDeclaration) => ({
      ...newDeclaration,
      [prop]: value,
    }));
  };

  const handleOrderChange = (_prop: string, value: any) => {
    const foundedOrder = orders.find((item) => item.id === value) as
      | ProcessingOrderDetails
      | undefined;

    setBatches(foundedOrder?.batches ?? []);
    setNewDeclaration((prev) => ({
      ...prev,
      orderId: value,
      order: foundedOrder,
      productType: foundedOrder?.plantType ?? prev.productType,
      sort: foundedOrder?.sort?.name ?? prev.sort,
    }));
  };

  const handleBatchChange = (_prop: string, value: any) => {
    const foundedBatch = batches.find((item) => item.id === value) as
      | ProcessingBatch
      | undefined;

    setNewDeclaration((prev) => ({
      ...prev,
      batchId: value,
      batch: foundedBatch,
      amountKg: foundedBatch?.planAmountKg ?? prev.amountKg,
    }));
  };

  let addButtonSaveStyle: React.CSSProperties = {};
  if (buttonSaveStyle) {
    addButtonSaveStyle = buttonSaveStyle;
  } else {
    addButtonSaveStyle.background = "rgba(0, 165, 83, 1)";
    addButtonSaveStyle.border = "none";
    addButtonSaveStyle.margin = "0 .5rem";
  }

  let addButtonResetStyle: React.CSSProperties = {};
  if (buttonResetStyle) {
    addButtonResetStyle = buttonResetStyle;
  } else {
    addButtonResetStyle.background = "rgba(220, 53, 69, 1)";
    addButtonResetStyle.border = "none";
  }

  return (
    <PageLayout>
      <Toast ref={toast} position={"top-right"} />
      <div className="form-wrapper">
        <div className="form-header">
          <div className="header-title">
            <i
              className="bi bi-file header-title-icon"
              style={{ fontSize: 18 }}
            ></i>
            <span className="header-title">
              {`${t("Deklaracija broj")}: `}
              {newDeclaration.declarationNumber ?? ""}
            </span>
          </div>
          <div className="header-buttons">
            <ButtonInput
              addStyle={{
                border: "1px solid #01a453",
                background: "#ffffff",
                color: "#01a453",
                margin: "0 .5rem",
              }}
              labelText={t("Sve deklaracije")}
              icon="bi bi-arrow-left-square"
              onButtonClick={() => {
                navigate(`/declarations`);
              }}
            />
            <ButtonInput
              addStyle={{
                border: "1px solid #01a453",
                background: "#ffffff",
                color: "#01a453",
                margin: "0 .5rem",
              }}
              labelText={""}
              icon="bi bi-file-earmark-pdf"
              onButtonClick={() => {
                if (newDeclaration.declarationNumber) {
                  if (downloadPdfDocument.current) {
                    // @ts-ignore
                    downloadPdfDocument.current();
                  }
                } else {
                  //@ts-ignore
                  toast.current.show({
                    severity: "error",
                    detail: t(
                      `Nije moguće preuzeti deklaraciju koja nije snimljena.`,
                    ),
                    life: 3000,
                  });
                }
              }}
            />
            <ButtonInput
              addStyle={{
                border: "1px solid #01a453",
                background: "#ffffff",
                color: "#01a453",
              }}
              labelText={""}
              icon="bi bi-printer"
              onButtonClick={() => {
                if (newDeclaration.declarationNumber) {
                  if (printPdfDocument.current) {
                    // @ts-ignore
                    printPdfDocument.current();
                  }
                } else {
                  //@ts-ignore
                  toast.current.show({
                    severity: "error",
                    detail: t(
                      `Nije moguće odštampati deklaraciju koja nije snimljena.`,
                    ),
                    life: 3000,
                  });
                }
              }}
            />
          </div>
        </div>
        {processing ? (
          <ProgressSpinner />
        ) : (
          <div className="form-body">
            <div className="form-input-fields">
              <TextInput
                id="declarationNumber"
                value={newDeclaration.declarationNumber ?? ""}
                onValueChange={handleNewDeclaration}
                labelText={`${t("Broj deklaracije *")}`}
                placeholderText={t("Unesite broj deklaracije")}
                control={control}
                setValue={setValue}
                errors={errors}
                rules={{ required: true }}
              />
              <DropdownInput
                id="order"
                value={newDeclaration.orderId}
                onValueChange={(_, v) => handleOrderChange("orderId", v)}
                optionValue="id"
                optionLabel="number"
                options={orders}
                filter={true}
                labelText={`${t("Nalog za doradu i pakovanje")} *`}
                placeholderText={t("Izaberite nalog za doradu i pakovanje")}
                control={control}
                setValue={setValue}
                errors={errors}
                rules={{ required: true }}
                disabled={loadingLookups}
              />
              <DropdownInput
                id="batch"
                value={newDeclaration.batchId}
                onValueChange={(_, v) => handleBatchChange("batchId", v)}
                optionValue="id"
                optionLabel="code"
                options={batches}
                filter={true}
                labelText={`${t("Partija")} *`}
                placeholderText={t("Izaberite partiju")}
                control={control}
                setValue={setValue}
                errors={errors}
                rules={{ required: true }}
                disabled={loadingLookups || !selectedOrder}
              />
              <div style={{ width: "100%" }} />
              <TextInput
                id="plantType"
                value={selectedOrder?.plantType ?? ""}
                onValueChange={handleNewDeclaration}
                labelText={`${t("Biljna vrsta")} *`}
                placeholderText={t("")}
                control={control}
                setValue={setValue}
                errors={errors}
                rules={{ required: true }}
                disabled={true}
              />
              <TextInput
                id="latinName"
                value={newDeclaration.latinName ?? ""}
                onValueChange={handleNewDeclaration}
                labelText={`${t("Latinski naziv")}`}
                placeholderText={t("Unesite latinski naziv")}
                control={control}
                setValue={setValue}
                errors={errors}
              />
              <TextInput
                id="sort"
                value={
                  selectedOrder?.sort
                    ? `${selectedOrder.sort.name ?? ""}`.trim()
                    : ""
                }
                onValueChange={handleNewDeclaration}
                labelText={`${t("Sorta")} *`}
                placeholderText={t("")}
                control={control}
                setValue={setValue}
                errors={errors}
                rules={{ required: false }}
                disabled={true}
              />
              <TextInput
                id="seedCategory"
                value={selectedOrder?.seedCategory ?? ""}
                onValueChange={handleNewDeclaration}
                labelText={`${t("Kategorija semena")} *`}
                placeholderText={t("")}
                control={control}
                setValue={setValue}
                errors={errors}
                rules={{ required: false }}
                disabled={true}
              />
              {selectedOrder?.request?.approvals &&
                selectedOrder.request.approvals.length > 0 &&
                selectedOrder.request.approvals.map(
                  (ap: any, index: number) => (
                    <Fragment key={ap.id ?? index}>
                      <TextInput
                        id={`approvalNumber_${index}`}
                        value={ap.number ?? ""}
                        onValueChange={() => {}}
                        labelText={
                          index === 0
                            ? (t("Broj aprobacionog uverenja") as string)
                            : ""
                        }
                        placeholderText=""
                        control={control}
                        setValue={setValue}
                        errors={errors}
                        disabled={true}
                      />
                      <DateInput
                        id={`approvalDate_${index}`}
                        value={ap.date ? new Date(ap.date as any) : new Date()}
                        onValueChange={() => {}}
                        labelText={
                          index === 0
                            ? (t("Datum aprobacionog uverenja") as string)
                            : ""
                        }
                        placeholderText=""
                        icon="bi bi-calendar-check"
                        control={control}
                        setValue={setValue}
                        errors={errors}
                        disabled={true}
                      />
                    </Fragment>
                  ),
                )}
              <TextInput
                id="planAmountKg"
                value={
                  Utility.formatAmount(newDeclaration?.batch?.planAmountKg) ??
                  ""
                }
                onValueChange={handleNewDeclaration}
                labelText={`${t("Količina semenske robe (kg)")} *`}
                placeholderText={t("")}
                control={control}
                setValue={setValue}
                errors={errors}
                rules={{ required: false }}
                disabled={true}
              />
              <TextInput
                id="packagesInBatch"
                value={
                  newDeclaration?.batch?.planAmountKg &&
                  selectedOrder?.packageCount
                    ? Utility.formatAmount(
                        newDeclaration?.batch?.planAmountKg /
                          parseInt(String(selectedOrder?.packingType)),
                      )
                    : ""
                }
                onValueChange={handleNewDeclaration}
                labelText={`${t("Broj pakovanja u partiji semena")} *`}
                placeholderText={t("")}
                control={control}
                setValue={setValue}
                errors={errors}
                rules={{ required: false }}
                disabled={true}
              />
              <TextInput
                id="packageNetMass"
                value={selectedOrder?.packingType ?? ""}
                onValueChange={handleNewDeclaration}
                labelText={`${t("Neto masa jednog pakovanja (kg)")} *`}
                placeholderText={t("")}
                control={control}
                setValue={setValue}
                errors={errors}
                rules={{ required: false }}
                disabled={true}
              />
              <TextInput
                id="treatment"
                value={selectedOrder?.treatment ?? ""}
                onValueChange={handleNewDeclaration}
                labelText={`${t("Preparat za tretman")} *`}
                placeholderText={t("")}
                control={control}
                setValue={setValue}
                errors={errors}
                rules={{ required: false }}
                disabled={true}
              />
              <TextInput
                id="productionYear"
                value={newDeclaration.productionYear ?? ""}
                onValueChange={handleNewDeclaration}
                labelText={`${t("Godina proizvodnje semena")}`}
                placeholderText={t("Unesite godinu proizvodnje semena")}
                control={control}
                setValue={setValue}
                errors={errors}
              />
              <TextInput
                id="labName"
                value={newDeclaration.labName ?? ""}
                onValueChange={handleNewDeclaration}
                labelText={`${t("Naziv organizacije koja vrši ispitivanje *")}`}
                placeholderText={t("Unesite naziv organizacije")}
                control={control}
                setValue={setValue}
                errors={errors}
                rules={{ required: true }}
              />
              <TextInput
                id="reportNumber"
                value={newDeclaration.reportNumber ?? ""}
                onValueChange={handleNewDeclaration}
                labelText={`${t("Broj izveštaja *")}`}
                placeholderText={t("Unesite broj izveštaja")}
                control={control}
                setValue={setValue}
                errors={errors}
                rules={{ required: true }}
              />
              <DateInput
                id="reportDate"
                value={
                  newDeclaration?.reportDate
                    ? new Date(newDeclaration.reportDate)
                    : null
                }
                onValueChange={handleNewDeclaration}
                labelText={`${t("Datum izveštaja *")}`}
                placeholderText={t("Unesite datum izveštaja")}
                icon="bi bi-calendar-check"
                control={control}
                setValue={setValue}
                errors={errors}
                rules={{ required: true }}
              />
              <TextInput
                id="purity"
                value={newDeclaration.purity ?? ""}
                onValueChange={handleNewDeclaration}
                labelText={`${t("Čistoća (%)")}`}
                placeholderText={t("Unesite čistoću u %")}
                control={control}
                setValue={setValue}
                errors={errors}
              />
              <TextInput
                id="moisture"
                value={newDeclaration.moisture ?? ""}
                onValueChange={handleNewDeclaration}
                labelText={`${t("Sadržaj vlage (%)")}`}
                placeholderText={t("Unesite sadržaj vlage u %")}
                control={control}
                setValue={setValue}
                errors={errors}
              />
              <TextInput
                id="germination"
                value={newDeclaration.germination ?? ""}
                onValueChange={handleNewDeclaration}
                labelText={`${t("Klijavost (%)")}`}
                placeholderText={t("Unesite klijavost u %")}
                control={control}
                setValue={setValue}
                errors={errors}
              />
              <TextInput
                id="germinationEnergy"
                value={newDeclaration.germinationEnergy ?? ""}
                onValueChange={handleNewDeclaration}
                labelText={`${t("Energija klijanja (%)")}`}
                placeholderText={t("Unesite energiju klijanja u %")}
                control={control}
                setValue={setValue}
                errors={errors}
              />
              <TextInput
                id="thousandSeedMass"
                value={newDeclaration.thousandSeedMass ?? ""}
                onValueChange={handleNewDeclaration}
                labelText={`${t("Masa 1000 semena")}`}
                placeholderText={t("Unesite masu 1000 semena")}
                control={control}
                setValue={setValue}
                errors={errors}
              />
              <TextInput
                id="otherSpecies"
                value={newDeclaration.otherSpecies ?? ""}
                onValueChange={handleNewDeclaration}
                labelText={`${t("Sadržaj drugih biljnih vrsta (%)")}`}
                placeholderText={t("Unesite sadržaj drugih biljnih vrsta u %")}
                control={control}
                setValue={setValue}
                errors={errors}
              />
              <TextInput
                id="weeds"
                value={newDeclaration.weeds ?? ""}
                onValueChange={handleNewDeclaration}
                labelText={`${t("Sadržaj korova (%)")}`}
                placeholderText={t("Unesite sadržaj korova  u %")}
                control={control}
                setValue={setValue}
                errors={errors}
              />
              <TextInput
                id="healthStatus"
                value={newDeclaration.healthStatus ?? ""}
                onValueChange={handleNewDeclaration}
                labelText={`${t("Zdravstveno stanje")}`}
                placeholderText={t("Unesite zdravstveno stanje")}
                control={control}
                setValue={setValue}
                errors={errors}
              />
              <DateInput
                id="issueDate"
                value={
                  newDeclaration?.issueDate
                    ? new Date(newDeclaration.issueDate)
                    : null
                }
                onValueChange={handleNewDeclaration}
                labelText={`${t("Datum izdavanja deklaracije *")}`}
                placeholderText={t("Unesite datum izdavanja deklaracije")}
                icon="bi bi-calendar-check"
                control={control}
                setValue={setValue}
                errors={errors}
                rules={{ required: true }}
              />
              <DateInput
                id="validUntil"
                value={
                  newDeclaration?.validUntil
                    ? new Date(newDeclaration.validUntil)
                    : null
                }
                onValueChange={handleNewDeclaration}
                labelText={`${t("Datum do kad važi deklaracija *")}`}
                placeholderText={t("Unesite datum važenja deklaracija")}
                icon="bi bi-calendar-check"
                control={control}
                setValue={setValue}
                errors={errors}
                rules={{ required: true }}
              />
              <TextInput
                id="labelsFrom"
                value={newDeclaration.labelsFrom ?? ""}
                onValueChange={handleNewDeclaration}
                labelText={`${t("Serijski brojevi etiketa (od)")}`}
                placeholderText={t("Unesite broj etiketa od")}
                control={control}
                setValue={setValue}
                errors={errors}
              />
              <TextInput
                id="labelsTo"
                value={newDeclaration.labelsTo ?? ""}
                onValueChange={handleNewDeclaration}
                labelText={`${t("Serijski brojevi etiketa (do)")}`}
                placeholderText={t("Unesite broj etiketa do")}
                control={control}
                setValue={setValue}
                errors={errors}
              />
              <div></div>
              <div className="footer-buttons">
                <ButtonInput
                  labelText={t("Sačuvaj")}
                  onButtonClick={handleSubmit(onSubmit, onError)}
                  addStyle={addButtonSaveStyle}
                />
                <ButtonInput
                  labelText={t("Resetuj")}
                  onButtonClick={handleResetForm}
                  addStyle={addButtonResetStyle}
                />
              </div>
            </div>
            <div className="documents-preview">
              {pdfData ? (
                <PdfPreview
                  pdfDefinition={pdfData}
                  downloadDocument={downloadPdfDocument}
                  printDocument={printPdfDocument}
                  documentName={`${t("Deklaracija o kvalitetu semena")}_${
                    newDeclaration?.declarationNumber
                  }.pdf`}
                />
              ) : (
                <p>Učitavam PDF prikaz...</p>
              )}
            </div>
          </div>
        )}
      </div>
    </PageLayout>
  );
};
